import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, MessageSquare, Bot, QrCode, Settings, Zap, CheckCircle2, ArrowRight, Play, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useLanguage } from '../hooks/useLanguage';
import { SEO } from '../components/SEO';
export function HowItWorksPage() {
  const {
    language
  } = useLanguage();
  const isRTL = language === 'ar';
  const steps = [{
    step: 1,
    icon: Sparkles,
    title: isRTL ? 'أنشئ Instance Name' : 'Create Instance Name',
    description: isRTL ? 'اكتب اسم البراند الخاص بك لتعريف البوت' : 'Write your brand name to identify your bot',
    details: isRTL ? ['اختر اسم فريد لعملك', 'سيظهر هذا الاسم في لوحة التحكم', 'يمكنك تغييره لاحقاً'] : ['Choose a unique name for your business', 'This name will appear in your dashboard', 'You can change it later'],
    color: 'from-purple-500 to-pink-500',
    bgColor: 'bg-purple-50'
  }, {
    step: 2,
    icon: QrCode,
    title: isRTL ? 'اربط واتساب بالـ QR' : 'Connect WhatsApp via QR',
    description: isRTL ? 'امسح كود QR لربط رقم واتساب الخاص بك' : 'Scan QR code to connect your WhatsApp number',
    details: isRTL ? ['افتح واتساب على هاتفك', 'اذهب إلى الأجهزة المرتبطة', 'امسح كود QR الظاهر'] : ['Open WhatsApp on your phone', 'Go to Linked Devices', 'Scan the QR code shown'],
    color: 'from-blue-500 to-purple-500',
    bgColor: 'bg-blue-50'
  }, {
    step: 3,
    icon: Settings,
    title: isRTL ? 'اكتب System Message' : 'Write System Message',
    description: isRTL ? 'حدد كيف يرد البوت على العملاء' : 'Define how the bot responds to customers',
    details: isRTL ? ['اكتب شخصية البوت', 'حدد المعلومات المهمة', 'اضغط Save لحفظ الإعدادات'] : ['Write the bot personality', 'Define important information', 'Click Save to save settings'],
    color: 'from-green-500 to-blue-500',
    bgColor: 'bg-green-50'
  }, {
    step: 4,
    icon: Zap,
    title: isRTL ? 'ابدأ الأتمتة!' : 'Start Automation!',
    description: isRTL ? 'البوت جاهز للرد على العملاء تلقائياً' : 'Bot is ready to respond to customers automatically',
    details: isRTL ? ['البوت يعمل 24/7', 'راقب المحادثات من الداشبورد', 'عدّل الإعدادات في أي وقت'] : ['Bot works 24/7', 'Monitor conversations from dashboard', 'Adjust settings anytime'],
    color: 'from-orange-500 to-red-500',
    bgColor: 'bg-orange-50'
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15
      }
    }
  };
  return <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50" dir={isRTL ? 'rtl' : 'ltr'} data-id="element-1933">
      <SEO title={isRTL ? 'كيف يعمل - ابدأ في 4 خطوات بسيطة' : 'How It Works - Start in 4 Simple Steps'} description={isRTL ? 'تعرف على كيفية عمل Operines: اربط واتساب، اكتب System Message، وابدأ الرد التلقائي على عملائك في دقائق معدودة.' : 'Learn how Operines works: connect WhatsApp, write your System Message, and start auto-replying to customers in minutes.'} keywords={isRTL ? 'كيف يعمل بوت واتساب, إعداد أتمتة, ربط واتساب, system message, خطوات البدء' : 'how WhatsApp bot works, setup automation, connect WhatsApp, system message, getting started steps'} canonicalPath="/how-it-works" language={language as 'ar' | 'en'} data-id="element-1934" />
      {/* Header */}
      <motion.header initial={{
      y: -100,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-200" data-id="element-1935">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1936">
          <div className="flex items-center justify-between py-4" data-id="element-1937">
            <Link to="/" className="flex items-center gap-2" data-id="element-1938">
              <img src="https://cdn.magicpatterns.com/uploads/8sRwXksp5brxBQY4QcgZ5M/U062aU0635U0645U064aU0645_U0628U062fU0648U0646_U0639U0646U0648U0627U0646_(37).png" alt="Operines" className="h-8 w-auto" data-id="element-1939" />
            </Link>
            <Link to="/" data-id="element-1940">
              <Button variant="outline" size="sm" data-id="element-1941">
                {isRTL ? <>
                    <ArrowRight className="w-4 h-4 ml-2" data-id="element-1942" />
                    العودة للرئيسية
                  </> : <>
                    <ArrowLeft className="w-4 h-4 mr-2" data-id="element-1943" />
                    Back to Home
                  </>}
              </Button>
            </Link>
          </div>
        </div>
      </motion.header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden" data-id="element-1944">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden" data-id="element-1945">
          <motion.div animate={{
          scale: [1, 1.2, 1],
          rotate: [0, 180, 360]
        }} transition={{
          duration: 20,
          repeat: Infinity,
          ease: 'linear'
        }} className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-to-br from-purple-100/50 to-pink-100/50 rounded-full blur-3xl" data-id="element-1946" />
          <motion.div animate={{
          scale: [1.2, 1, 1.2],
          rotate: [360, 180, 0]
        }} transition={{
          duration: 25,
          repeat: Infinity,
          ease: 'linear'
        }} className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-to-tr from-blue-100/50 to-green-100/50 rounded-full blur-3xl" data-id="element-1947" />
        </div>

        <div className="max-w-4xl mx-auto px-4 text-center relative z-10" data-id="element-1948">
          <motion.div initial={{
          scale: 0,
          rotate: -180
        }} animate={{
          scale: 1,
          rotate: 0
        }} transition={{
          type: 'spring',
          stiffness: 200,
          damping: 20
        }} className="w-20 h-20 bg-gradient-to-br from-[#4f1964] to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-2xl" data-id="element-1949">
            <Bot className="w-10 h-10 text-white" data-id="element-1950" />
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6" data-id="element-1951">
            {isRTL ? 'كيف يعمل؟' : 'How It Works?'}
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1952">
            {isRTL ? 'ابدأ في دقائق معدودة مع 4 خطوات بسيطة' : 'Get started in minutes with 4 simple steps'}
          </motion.p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-16 relative" data-id="element-1953">
        <div className="max-w-6xl mx-auto px-4" data-id="element-1954">
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="space-y-8" data-id="element-1955">
            {steps.map((step, index) => <motion.div key={step.step} variants={itemVariants} className={`relative ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`} data-id="element-1956">
                <div className={`flex flex-col md:flex-row items-center gap-8 ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`} data-id="element-1957">
                  {/* Step Number & Icon */}
                  <motion.div whileHover={{
                scale: 1.1,
                rotate: 5
              }} className="relative flex-shrink-0" data-id="element-1958">
                    <div className={`w-32 h-32 rounded-3xl bg-gradient-to-br ${step.color} flex items-center justify-center shadow-2xl`} data-id="element-1959">
                      <step.icon className="w-16 h-16 text-white" data-id="element-1960" />
                    </div>
                    <motion.div initial={{
                  scale: 0
                }} whileInView={{
                  scale: 1
                }} viewport={{
                  once: true
                }} transition={{
                  delay: 0.3,
                  type: 'spring'
                }} className="absolute -top-3 -right-3 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-xl font-bold text-[#4f1964]" data-id="element-1961">
                      {step.step}
                    </motion.div>
                  </motion.div>

                  {/* Content */}
                  <div className={`flex-1 ${step.bgColor} rounded-3xl p-8 shadow-lg`} data-id="element-1962">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1963">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 mb-6" data-id="element-1964">{step.description}</p>
                    <ul className="space-y-3" data-id="element-1965">
                      {step.details.map((detail, i) => <motion.li key={i} initial={{
                    opacity: 0,
                    x: -20
                  }} whileInView={{
                    opacity: 1,
                    x: 0
                  }} viewport={{
                    once: true
                  }} transition={{
                    delay: 0.1 * i
                  }} className="flex items-center gap-3" data-id="element-1966">
                          <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" data-id="element-1967" />
                          <span className="text-gray-700" data-id="element-1968">{detail}</span>
                        </motion.li>)}
                    </ul>
                  </div>
                </div>

                {/* Connector Line */}
                {index < steps.length - 1 && <motion.div initial={{
              scaleY: 0
            }} whileInView={{
              scaleY: 1
            }} viewport={{
              once: true
            }} className="hidden md:block absolute left-1/2 -translate-x-1/2 top-full w-1 h-8 bg-gradient-to-b from-purple-300 to-transparent" data-id="element-1969" />}
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-16 bg-gradient-to-r from-purple-50 to-pink-50" data-id="element-1970">
        <div className="max-w-4xl mx-auto px-4" data-id="element-1971">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-10" data-id="element-1972">
            <motion.div animate={{
            rotate: [0, 10, -10, 0]
          }} transition={{
            duration: 2,
            repeat: Infinity
          }} className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 text-red-600 rounded-full text-sm font-medium mb-4" data-id="element-1973">
              <Play className="w-4 h-4" data-id="element-1974" />
              {isRTL ? 'شاهد الفيديو' : 'Watch Video'}
            </motion.div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4" data-id="element-1975">
              {isRTL ? 'شاهد الشرح المفصل' : 'Watch Detailed Tutorial'}
            </h2>
            <p className="text-gray-600" data-id="element-1976">
              {isRTL ? 'تعلم كيفية إعداد البوت خطوة بخطوة' : 'Learn how to set up the bot step by step'}
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} whileHover={{
          scale: 1.02
        }} className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white" data-id="element-1977">
            <div className="aspect-video" data-id="element-1978">
              <iframe src="https://www.youtube.com/embed/2dfaGlCUr5Y" title="Operines Tutorial" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" data-id="element-1979"></iframe>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" data-id="element-1980">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-1981">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-1982">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6" data-id="element-1983">
              {isRTL ? 'جاهز للبدء؟' : 'Ready to Start?'}
            </h2>
            <p className="text-xl text-gray-600 mb-8" data-id="element-1984">
              {isRTL ? 'ابدأ تجربتك المجانية لمدة 7 أيام الآن' : 'Start your 7-day free trial now'}
            </p>
            <Link to="/register" data-id="element-1985">
              <motion.div whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} data-id="element-1986">
                <Button size="lg" className="bg-[#4f1964] hover:bg-[#3d1350] text-white px-12 py-4 text-lg shadow-xl" data-id="element-1987">
                  {isRTL ? 'ابدأ الآن مجاناً' : 'Start Now Free'}
                </Button>
              </motion.div>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>;
}